import { environment } from "./../../../../environments/environment";
import { inject, Injectable } from "@angular/core";
import {
  Auth,
  authState,
  createUserWithEmailAndPassword,
  getRedirectResult,
  idToken,
  OAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  updateProfile,
  User,
  user,
  UserCredential,
} from "@angular/fire/auth";
import {
  BehaviorSubject,
  catchError,
  from,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";
import { LogService } from "../../../shared/service/logging/log.service";
@Injectable({
  providedIn: "root",
})
export class FirebaseService {
  firebaseAuth = inject(Auth);
  logger = inject(LogService);

  // Triggered by sign-in, sign-out and token refresh events
  public readonly idToken$: Observable<string | null> = idToken(
    this.firebaseAuth,
  );

  public updateUsername(displayName: string) {
    const user = this.firebaseAuth.currentUser;

    if (!user) {
      throw new Error("No user logged in");
    }

    return from(updateProfile(user, { displayName }));
  }

  // Triggered by sign-in, sign-out and token refresh events
  public readonly firebaseUser$: Observable<User | null> = user(
    this.firebaseAuth,
  );

  // Only triggered on sign-in and sign-out
  public readonly authState$: Observable<User | null> = authState(
    this.firebaseAuth,
  );

  microsoftProvider = this.getMicrosoftProvider();

  public userCredential$: BehaviorSubject<UserCredential | null> =
    new BehaviorSubject(null);

  public msfAccessToken$: BehaviorSubject<string | null> = new BehaviorSubject(
    null,
  );

  public readonly userCredential = toSignal(this.userCredential$);
  public readonly msfAccessToken = toSignal(this.msfAccessToken$);

  private createOAuthProvider(
    providerId: string,
    tenantId: string,
    scopes: string[],
  ): OAuthProvider {
    const provider = new OAuthProvider(providerId);
    provider.setCustomParameters({
      tenant: tenantId,
      prompt: "select_account",
    });
    scopes.forEach((scope) => provider.addScope(scope));
    return provider;
  }

  private getMicrosoftProvider(): OAuthProvider {
    return this.createOAuthProvider(
      environment.microsoftProviderConfig.provider,
      environment.microsoftProviderConfig.tenant,
      environment.microsoftProviderConfig.scopes,
    );
  }

  register(
    email: string,
    displayName: string,
    password: string,
  ): Observable<UserCredential> {
    return this.saveUserCredential(
      from(
        createUserWithEmailAndPassword(this.firebaseAuth, email, password),
      ).pipe(
        switchMap((userCredential) =>
          from(updateProfile(userCredential.user, { displayName })).pipe(
            switchMap(() => from(userCredential.user.getIdToken(true))),
            map(() => userCredential),
          ),
        ),
      ),
    );
  }

  login(email: string, password: string): Observable<UserCredential> {
    return this.saveUserCredential(
      from(signInWithEmailAndPassword(this.firebaseAuth, email, password)),
    );
  }

  logout(): Observable<void> {
    return from(signOut(this.firebaseAuth));
  }

  loginMicrosoftPopup(): Observable<string | null> {
    return this.processMicrosoftLogin(
      from(signInWithPopup(this.firebaseAuth, this.microsoftProvider)),
    );
  }

  // TODO: Fix redirect
  loginMicrosoftRedirect(): void {
    signInWithRedirect(this.firebaseAuth, this.microsoftProvider).catch(
      (error) => {
        this.logger.error("Microsoft redirect login error:", error);
      },
    );
  }

  // TODO: Fix redirect
  handleRedirectResult(): Observable<string | null> {
    return this.processMicrosoftLogin(
      from(getRedirectResult(this.firebaseAuth)),
    );
  }

  private processMicrosoftLogin(
    req: Observable<UserCredential>,
  ): Observable<string | null> {
    return this.saveUserCredential(req).pipe(
      switchMap((userCredential) =>
        this.saveMsfAccessToken(of(userCredential)),
      ),
    );
  }

  private saveUserCredential(
    req: Observable<UserCredential>,
  ): Observable<UserCredential> {
    return req.pipe(
      tap((userCredential) => {
        if (userCredential?.user) {
          this.userCredential$.next(userCredential);
        }
      }),
      catchError((err) => {
        this.logger.error("Error saving user credentials:", err);
        throw err;
      }),
    );
  }

  private saveMsfAccessToken(
    req: Observable<UserCredential>,
  ): Observable<string | null> {
    return req.pipe(
      map((userCredential) => {
        const credential = OAuthProvider.credentialFromResult(userCredential);
        const accessToken = credential?.accessToken || null;
        if (accessToken) {
          this.msfAccessToken$.next(accessToken);
        }
        return accessToken;
      }),
      catchError((err) => {
        this.logger.error("Error saving Microsoft access token:", err);
        throw err;
      }),
    );
  }
}
